import React, { useEffect, useState } from "react";
import axios from "axios";
import { Edit, Link, Search, Trash2, Plus, Download, FileText } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { 
  Container, 
  Row, 
  Col, 
  Table, 
  Spinner, 
  Modal, 
  InputGroup, 
  Form, 
  Button, 
  OverlayTrigger, 
  Popover 
} from "react-bootstrap";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { getLabCategory } from "../../../../../redux/lab";
import LabCategoryEntry from "./LabCategoryEntry";

// Framer motion variants for clean layout loads
const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const LabCategory = (props) => {
  const itemsList = useSelector((state) => state.itemsList);
  const lab = useSelector((state) => state.lab);
  const [editData, setEditData] = useState("");
  const dispatch = useDispatch();
  const { labCategory } = lab;
  const { itemsData, supplierData, purchaseData } = itemsList;
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [alertCount, setAlertCount] = useState("");
  const [formData, setFormData] = useState("");
  const [modal, setModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [procedureDataDownload, setProcedureDataDownload] = useState("");
  const [procedures, setProcedures] = useState("");
  const toggle = () => setModal(!modal);
  const [popoverData, setPopoverData] = useState("");

  const getLabCategoryHandler = () => {
    dispatch(
      getLabCategory({
        token: userData.token,
        branch: userData.branch,
      })
    );
  };

  useEffect(() => {
    if (userData.branch && userData.token) getLabCategoryHandler();
  }, [userData.branch, userData.token]);

  useEffect(() => {
    if (labCategory && labCategory.length) {
      setProcedureDataDownload(() =>
        labCategory.filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (t) => t.testname === item.testname && t.method === item.method
            )
        )
      );
    }
  }, [labCategory]);

  const exportToExcel = (data) => {
    if (!data || !data.length) return;
    const transformedData = data.map((item, index) => {
      const { _id, ...rest } = item;
      return { "Sl.No": index + 1, ...rest };
    });

    const ws = XLSX.utils.json_to_sheet(transformedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "my-file.xlsx");
  };

  const editCategoryHandler = (Data, toggle) => {
    setFormData(Data);
    setEditData(Data);
    toggle();
  };

  const deleteHandler = (item) => {
    const categoryName = prompt(
      "Please enter the category name to confirm delete"
    );
    if (
      categoryName &&
      categoryName.toLocaleLowerCase() === item.categoryName.toLocaleLowerCase()
    ) {
      axios
        .delete(`${process.env.REACT_APP_CRI_API}delete-lab-cat`, {
          params: { id: item._id, branch: userData.branch },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        })
        .then((res) => {
          if (res) {
            getLabCategoryHandler();
          }
        })
        .catch((err) => {
          if (
            (err.response.data &&
              err.response.data.err &&
              err.response.data.err.name === "TokenExpiredError") ||
            err.response.data.message === "Unauthorized"
          ) {
            navigate("/");
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("srchData");
            sessionStorage.removeItem("prescriptionHistory");
            sessionStorage.removeItem("rights");
            navigate("/cri-crm");
            return err;
          }
        });
    } else if (categoryName) {
      alert("Batch not matching");
    }
  };

  // Modernized Notes Popover Template
  const renderNotesPopover = (notes) => (
    <Popover id="popover-notes" className="shadow border-0">
      <Popover.Header className="bg-light fw-bold text-secondary py-2 border-bottom">Notes</Popover.Header>
      <Popover.Body className="p-0">
        <Table size="sm" className="mb-0 text-muted">
          <thead>
            <tr className="bg-light small text-uppercase tracking-wider">
              <th className="px-3 py-2 border-0"></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-0 small">{notes}</td>
            </tr>
          </tbody>
        </Table>
      </Popover.Body>
    </Popover>
  );

  return (
    <Container fluid className="px-0">
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        
        {/* Top Control Bar Action Row */}
        <Row className="align-items-center g-3 mb-4">
          <Col md={4} sm={6} xs={12}>
            <Button
              variant="primary"
              className="d-inline-flex align-items-center gap-2 px-3 py-2 shadow-sm rounded-2 fw-medium w-sm-auto w-100 justify-content-center"
              onClick={() => {
                setEditData("");
                toggle();
              }}
            >
              <Plus size={16} /> Add New Category
            </Button>
          </Col>
          
          <Col md={5} sm={6} xs={12}>
            <InputGroup className="shadow-sm rounded-2 overflow-hidden">
              <Form.Control
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search Test Name..."
                className="border-end-0 py-2"
              />
              <InputGroup.Text className="bg-white border-start-0 text-muted">
                <Search size={16} />
              </InputGroup.Text>
            </InputGroup>
          </Col>

          <Col md={3} xs={12} className="text-md-end">
            <Button
              variant="outline-secondary"
              className="d-inline-flex align-items-center gap-2 px-3 py-2 shadow-sm rounded-2 bg-white w-md-auto w-100 justify-content-center text-secondary border-light-subtle"
              onClick={() => exportToExcel(procedureDataDownload)}
            >
              <Download size={16} /> Download Excel
            </Button>
          </Col>
        </Row>

        {/* Data Presentation Table Section */}
        <Row>
          <Col md={12}>
            {!labCategory && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            )}
            
            {labCategory && labCategory.length > 0 && (
              <div className="card border-0 shadow-sm rounded-3 overflow-hidden">

                <Table responsive hover className="align-middle mb-0 text-nowrap" style={{ tableLayout: 'fixed', width: '100%' }}>
                  
                  <thead className="table-light border-bottom border-light-subtle text-secondary small text-uppercase font-monospace">
                    <tr>
                      <th className="py-3 px-3">Sl. No.</th>
                      <th className="py-3">Category</th>
                      <th className="py-3" style={{ width: '200px' }}>Test Name</th>
                      <th className="py-3" style={{ width: '200px' }}>Sub Test Name</th>
                      <th className="py-3" style={{ width: '200px' }}>Specimen</th>
                      <th className="py-3" style={{ width: '300px' }}>Reference Range</th>
                      <th className="py-3">Units</th>
                      <th className="py-3">Method</th>
                      <th className="py-3 text-center">Notes</th>
                      <th className="py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labCategory.map((cat, categoryIndex) => (
                      <React.Fragment key={cat._id || categoryIndex}>
                        
                        {/* Parent Category Header Row */}
                        <tr className="table-group-divider bg-light bg-opacity-50 fw-semibold">
                          <td className="px-3 text-secondary">{categoryIndex + 1}</td>
                          <td className="text-dark fw-bold text-uppercase small tracking-wide" colSpan={7}>
                            {cat.categoryName}
                          </td>
                          <td></td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <Button 
                                variant="link" 
                                className="p-1 text-success link-offset-2 link-underline-opacity-0 link-underline-opacity-100-hover"
                                onClick={() => editCategoryHandler(cat, toggle)}
                              >
                                <Edit size={15} />
                              </Button>
                              <Button 
                                variant="link" 
                                className="p-1 text-danger link-offset-2 link-underline-opacity-0 link-underline-opacity-100-hover"
                                onClick={() => deleteHandler(cat)}
                              >
                                <Trash2 size={15} />
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {/* Secondary Tests Loop nested matrix */}
                        {cat.tests.map((test, testIndex) => (
                          <React.Fragment key={test._id || testIndex}>
                            <tr className="align-middle">
                              <td></td>
                              <td></td>
                              <td className="fw-medium text-dark">{test.testName}</td>
                              <td></td>
                              <td className="text-muted small">{test.specimen}</td>
                              <td className="font-monospace text-secondary small text-wrap" style={{ width: '250px' }}>{test.referenceRange}</td>
                              <td><span className="badge bg-light border text-dark fw-normal">{test.units}</span></td>
                              <td className="text-muted small text-truncate" style={{ maxWidth: "200px" }}>{test.method}</td>
                              <td className="text-center">
                                {test.notes && (
                                  <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderNotesPopover(test.notes)}>
                                    <Button variant="link" className="p-0 text-primary d-inline-flex align-items-center">
                                      <FileText size={15} />
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </td>
                              <td></td>
                            </tr>

                            {/* Leaf Level Subtest Row Matrix */}
                            {test.subTests && test.subTests.length > 0 && test.subTests.map((subTest, subTestIndex) => (
                              <tr key={subTestIndex} className="table-sm-row text-muted bg-neutral bg-opacity-10">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="ps-4 text-secondary small-text-sub">↳ {subTest.subTestName}</td>
                                <td className="small">{subTest.specimen}</td>
                                <td className="font-monospace small" style={{ width: '250px' }}>{subTest.referenceRange}</td>
                                <td><span className="badge bg-light text-muted fw-normal">{subTest.units}</span></td>
                                <td className="small text-truncate" style={{ maxWidth: "200px" }}>{subTest.method}</td>
                                <td className="text-center">
                                  {subTest.notes && (
                                    <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderNotesPopover(subTest.notes)}>
                                      <Button variant="link" className="p-0 text-primary d-inline-flex align-items-center">
                                        <FileText size={14} />
                                      </Button>
                                    </OverlayTrigger>
                                  )}
                                </td>
                                <td></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>

        {/* Action Form Entry Fullscreen Workspace Panel Modal */}
        <Modal show={modal} onHide={toggle} fullscreen className="border-0">
          <Modal.Header closeButton className="bg-light border-0 px-4 py-3">
            <Modal.Title className="h5 mb-0 fw-bold text-dark">
              {editData ? "Edit Category Setup" : "Create New Procedure Configuration"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 bg-white">
            <LabCategoryEntry
              formData={formData}
              setFormData={setFormData}
              getLabItemsHandler={getLabCategoryHandler}
              toggle={toggle}
              editData={editData}
            />
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 px-4 py-2">
            <Button variant="secondary" size="sm" className="px-3 rounded-2" onClick={toggle}>
              Close Window
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Global Inline Intercept processing lock loader modal popup */}
        <Modal show={showModal} centered size="sm" backdrop="static" keyboard={false} className="border-0 bg-transparent shadow-none">
          <Modal.Body className="text-center p-4 bg-white rounded-3 shadow">
            <Spinner animation="border" variant="primary" style={{ width: "2.5rem", height: "2.5rem" }} className="mb-3" />
            <p className="mb-0 text-dark fw-bold small">Saving Configurations...</p>
          </Modal.Body>
        </Modal>

      </motion.div>
    </Container>
  );
};

export default LabCategory;